import type React from 'react';
import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import clsx from 'clsx';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import * as T from 'fp-ts/Task';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import ThreeDotsIcon from '../assets/images/threeDots.svg';
import { ErrorInterrupter } from '../components/ErrorInterrupter';
import { BottomMobileMenu } from '../components/menu/BottomMobileMenu';
import PartnerLogo from '../components/PartnerLogo';
import { LoadingFallback } from '../components/progress/LoadingFallback';
import { useIsAssessmentCompleted } from '../hooks/useIsAssessmentCompleted';
import type { User } from '../model/domain/User';
import { agreementState } from '../model/state/agreementState';
import { sideMenuForceOpen } from '../model/state/sideMenuState';
import { themeState } from '../model/state/ThemeState';
import { userState } from '../model/state/userState';
import type { BSTask } from '../model/types';
import AuthService from '../service/AuthService';
import { analytics } from '../util/analytics/AnalyticsController';
import { debug } from '../util/logger';

import { PrivacyPolicy } from './PrivacyPolicy';
import { PasswordPreferencesPage } from './SubMenuPage';

const SignIn = lazy(() => import('./SignIn'));
const WelcomeBack = lazy(() => import('./welcomeBack/WelcomeBackPage'));
const Tour = lazy(() => import('./Tour'));
const StudyPage = lazy(() => import('./study/StudyPage'));
const CongratulationPage = lazy(() => import('./congratulation/CongratulationPage'));
const SignUp = lazy(() => import('./SignUp'));
const PasswordReset = lazy(() => import('./PasswordReset'));
const ChangePassword = lazy(() => import('./ChangePassword'));
const UpdatePassword = lazy(() => import('./UpdatePassword'));
const Feedback = lazy(() => import('./Feedback'));
const Settings = lazy(() => import('./Settings'));
const PerformancePage = lazy(() => import('./performance/PerformancePage'));
const LogoutPage = lazy(() => import('./Logout'));
const LibraryPage = lazy(() => import('./library/LibraryPage'));
const LibraryDeckPage = lazy(() => import('./library-deck/LibraryDeckPage'));
const LibraryTour = lazy(() => import('./LibraryTour'));

const excludedPathsFromInitNavigation = ['/signup', '/updatePws', '/pws', '/privacy-policy'];

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useAtom(userState);
  const [isLoading, setLoading] = useState(true);
  const isLoggedIn = useMemo(() => O.isSome(user), [user]);
  const setAgreement = useSetAtom(agreementState);
  const theme = useAtomValue(themeState);
  const [forceShowMenu, setForceShowMenu] = useAtom(sideMenuForceOpen);
  const isAssessmentCompleted = useIsAssessmentCompleted();

  const signInWithTokenTask = useCallback<() => BSTask<User>>(() => pipe(AuthService.signInWithToken()), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable
  const onSignInError = useCallback(() => {
    if (location.pathname !== '/privacy-policy') {
      navigate('/');
    }
    setUser(O.none);
  }, [navigate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are stable
  const onSignInOk = useCallback(
    (u: User): void => {
      setUser(O.some(u));
      if (u.partner.showTermOfUse) {
        setAgreement(u.partner.termOfUseUrl);
      }
      if (location.pathname !== '/privacy-policy') navigate('/welcomeBack');
    },
    [setUser, navigate],
  );

  const signInOnStartup = useCallback(async () => {
    debug(`Signing in on startup`);

    return pipe(signInWithTokenTask(), T.map(E.fold(onSignInError, onSignInOk)), (invokeTask: T.Task<void>) =>
      invokeTask(),
    );
  }, [signInWithTokenTask, onSignInError, onSignInOk]);

  const shouldSignInOnStartup = () => {
    debug('location.pathname=', location.pathname);
    let res = false;
    const doNotSignInOnPaths = ['/pws', '/signup', '/resetPws', '/privacy-policy'];
    doNotSignInOnPaths.forEach((path) => {
      res = res || location.pathname.startsWith(path);
    });
    debug('res: ', res, location);

    return !res;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: should run only once on mount
  useEffect(() => {
    if (!excludedPathsFromInitNavigation.some((path) => location.pathname.includes(path))) {
      navigate('/');
    }

    (async function foo() {
      setLoading(true);
      if (shouldSignInOnStartup()) {
        await signInOnStartup();
      }
      setLoading(false);
    })();
  }, []);

  const enableUserPerformance = pipe(
    user,
    O.fold(
      () => false,
      (u) => u.partner.enableUserPerformance,
    ),
  );

  const enableLibrary = pipe(
    user,
    O.fold(
      () => false,
      (u) => u.partner.enableLibrary,
    ),
  );

  useEffect(() => {
    document.title = 'Blank Slate';
  }, []);

  useEffect(() => {
    const currentUser = O.toUndefined(user);

    if (currentUser) analytics.identifyUser(currentUser);
    else analytics.resetUser();
  }, [user]);

  const appRoutes = useMemo(() => {
    return (
      <Routes>
        <Route element={<SignIn />} path={'/'} />
        <Route element={<WelcomeBack />} path={'/welcomeBack'} />
        <Route element={<StudyPage />} path={'/study'} />
        <Route element={<UpdatePassword />} path={'/updatePws'} />
        <Route element={<PrivacyPolicy />} path={'/privacy-policy'} />
        <Route element={<Tour />} path={'/tour'} />
        <Route element={<Feedback />} path={'/feedback'} />
        <Route element={<CongratulationPage />} path={'/congratulation'} />
        <Route element={<Settings />} path={'/preferences'} />
        <Route element={<PasswordPreferencesPage />} path={'/settings'} />
        {enableUserPerformance && isAssessmentCompleted && (
          <Route element={<PerformancePage />} path={'/performance'} />
        )}
        {enableLibrary && isAssessmentCompleted ? <Route element={<LibraryTour />} path={'/library_tour'} /> : null}
        {enableLibrary && isAssessmentCompleted && (
          <Route path={'/library'}>
            <Route element={<LibraryDeckPage />} path={'/library/deck/:deckId'} />
            <Route element={<LibraryPage />} path={'/library'} />
          </Route>
        )}
        <Route element={<LogoutPage />} path={'/logout'} />
        <Route element={<Navigate replace to="/welcomeBack" />} path={'*'} />
      </Routes>
    );
  }, [isAssessmentCompleted, enableUserPerformance, enableLibrary]);

  const authRoutes = useMemo(
    () => (
      <>
        <Routes>
          <Route element={<SignIn />} path={'/'} />
          <Route element={<SignIn />} path={'/signin'} />
          <Route element={<SignUp />} path={'/signup'} />
          <Route element={<PasswordReset />} path={'/resetPws'} />
          <Route element={<PrivacyPolicy />} path={'/privacy-policy'} />
          <Route element={<ChangePassword />} path={'/pws'} />
        </Routes>
      </>
    ),
    [],
  );

  const partnerLogo = pipe(
    user,
    O.fold(
      () => <span />,
      (u: User) => <PartnerLogo partner={u.partner} />,
    ),
  );

  const showMobileMenu = () => {
    setForceShowMenu(!forceShowMenu);
  };

  return (
    <div
      className={clsx(
        'relative flex h-screen-safe w-full flex-grow flex-col items-stretch justify-between lg:p-0',
        theme === 'light'
          ? 'bg-surface-default-main lg:bg-surface-gray-soft'
          : 'bg-gradient-slate-vertical lg:bg-bgWhite lg:bg-none',
      )}
    >
      {isLoading ? (
        <LoadingFallback />
      ) : (
        <>
          {isLoggedIn && (
            <div className="flex h-[60px] min-h-[60px] flex-row-reverse items-center justify-between px-10 lg:flex-row lg:px-0">
              <div className="h-max">
                <button aria-label="mobile menu" className="lg:hidden" onClick={showMobileMenu} type="button">
                  <img
                    alt="mobile menu"
                    // onClick={showMobileMenu}
                    src={ThreeDotsIcon}
                  />
                </button>
              </div>
              <div className="flex items-center justify-end">{partnerLogo}</div>
            </div>
          )}
          <ErrorInterrupter>
            <div className="flex h-full justify-center overflow-y-auto lg:items-center">
              {isLoggedIn ? appRoutes : authRoutes}
            </div>
          </ErrorInterrupter>
          {isLoggedIn && location.pathname !== '/study' && isAssessmentCompleted ? <BottomMobileMenu /> : null}
        </>
      )}
    </div>
  );
};

export default Layout;
